/**
 * product-description-tabs.js
 *
 * Converts h4 elements inside .product__description into an accessible tab UI.
 * Self-contained IIFE — no global scope pollution, exits early if the element
 * doesn't exist on the page. Safe to load on any template.
 *
 * Load in product template:
 *   <script src="{{ 'product-description-tabs.js' | asset_url }}" defer></script>
 */

(function () {
  const container = document.querySelector('.product__description');
  if (!container) return;

  const headings = [...container.querySelectorAll('h4')];
  if (headings.length < 2) return; // Don't tab a single section — just show it

  // ── Build tab data ──────────────────────────────────────────
  // Each tab gets its label from the h4 and collects all sibling
  // nodes between itself and the next h4 as its panel content.

  const tabs = headings.map((h4, i) => {
    const nodes = [];
    let next = h4.nextSibling;
    const nextHeading = headings[i + 1];

    while (next && next !== nextHeading) {
      nodes.push(next.cloneNode(true));
      next = next.nextSibling;
    }

    return { label: h4.textContent.trim(), nodes };
  });

  // ── Build tab nav ───────────────────────────────────────────

  const tabList = document.createElement('div');
  tabList.className = 'pd-tabs__nav';
  tabList.setAttribute('role', 'tablist');

  // ── Build panels ────────────────────────────────────────────

  const panels = tabs.map(({ label, nodes }, i) => {
    const btn = document.createElement('button');
    btn.className = 'pd-tabs__btn';
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
    btn.setAttribute('aria-controls', `pd-tab-panel-${i}`);
    btn.setAttribute('id', `pd-tab-btn-${i}`);
    btn.setAttribute('type', 'button');
    btn.textContent = label;
    tabList.appendChild(btn);

    const panel = document.createElement('div');
    panel.className = 'pd-tabs__panel';
    panel.setAttribute('role', 'tabpanel');
    panel.setAttribute('id', `pd-tab-panel-${i}`);
    panel.setAttribute('aria-labelledby', `pd-tab-btn-${i}`);
    panel.setAttribute('tabindex', '0');
    panel.hidden = i !== 0;
    nodes.forEach((node) => panel.appendChild(node));

    return { btn, panel };
  });

  // ── Wire click handlers ─────────────────────────────────────

  panels.forEach(({ btn }, i) => {
    btn.addEventListener('click', () => {
      panels.forEach(({ btn: b, panel: p }, j) => {
        const active = j === i;
        b.setAttribute('aria-selected', String(active));
        b.classList.toggle('pd-tabs__btn--active', active);
        p.hidden = !active;
      });
    });
  });

  // ── Keyboard navigation (arrows) ────────────────────────────
  // Left/right arrow keys move between tabs per ARIA pattern.

  tabList.addEventListener('keydown', (e) => {
    const btns = panels.map(({ btn }) => btn);
    const current = btns.indexOf(document.activeElement);
    if (current === -1) return;

    let target = -1;
    if (e.key === 'ArrowRight') target = (current + 1) % btns.length;
    if (e.key === 'ArrowLeft') target = (current - 1 + btns.length) % btns.length;
    if (e.key === 'Home') target = 0;
    if (e.key === 'End') target = btns.length - 1;

    if (target !== -1) {
      e.preventDefault();
      btns[target].click();
      btns[target].focus();
    }
  });

  // ── Replace container contents ──────────────────────────────

  container.innerHTML = '';
  container.appendChild(tabList);
  panels.forEach(({ panel }) => container.appendChild(panel));

  // Set initial active state
  panels[0].btn.classList.add('pd-tabs__btn--active');
})();
